/**
 * Read a .xlsx or .csv in the browser, with no dependency.
 *
 * WHY NOT SHEETJS. The `xlsx` package on npm is pinned at a version carrying
 * open advisories (prototype pollution, ReDoS) because SheetJS moved their
 * current releases off npm. Pulling that into an app that already handles
 * client data, to read a file the user could equally save as CSV, is a poor
 * trade. An .xlsx is a zip of XML and the browser can already inflate a zip
 * — `DecompressionStream('deflate-raw')` has been in every major engine since
 * 2023 — so the whole reader is about a hundred and fifty lines of it.
 *
 * WHAT THIS DELIBERATELY DOES NOT DO. It reads cell VALUES from the first
 * worksheet: no formulas, no formatting, no merged-cell expansion, no dates as
 * Date objects. A ticket export is a flat grid of text and that is all the
 * audit needs. Anything it cannot read it says so about, rather than returning
 * a half-parsed grid that would quietly under-report findings.
 */

export interface Sheet {
  /** The header row, trimmed. */
  headers: string[];
  /** Every row after the header, as strings aligned to `headers`. */
  rows: string[][];
  /** Which sheet this came from, for the "checked N rows of X" line. */
  sheetName: string | null;
}

/* -------------------------------------------------------------------------
   Zip
   ------------------------------------------------------------------------- */

const dv = (b: ArrayBuffer) => new DataView(b);

/**
 * Locate the End of Central Directory record.
 *
 * Scanned backwards from the end because the record is last and carries a
 * trailing comment of arbitrary length, so its offset cannot be computed. The
 * 64KB window is the maximum that comment can be.
 */
function findEocd(buf: ArrayBuffer): number {
  const view = dv(buf);
  const min = Math.max(0, buf.byteLength - 0xffff - 22);
  for (let i = buf.byteLength - 22; i >= min; i -= 1) {
    if (view.getUint32(i, true) === 0x06054b50) return i;
  }
  return -1;
}

/** Every entry in the archive, by name, as raw compressed bytes plus method. */
function readCentralDirectory(
  buf: ArrayBuffer,
): Map<string, { method: number; offset: number; size: number }> {
  const view = dv(buf);
  const eocd = findEocd(buf);
  if (eocd < 0) throw new Error('That file is not a valid .xlsx (no zip directory found).');

  const count = view.getUint16(eocd + 10, true);
  let p = view.getUint32(eocd + 16, true);
  const out = new Map<string, { method: number; offset: number; size: number }>();

  for (let i = 0; i < count; i += 1) {
    if (view.getUint32(p, true) !== 0x02014b50) break;
    const method = view.getUint16(p + 10, true);
    const size = view.getUint32(p + 20, true);
    const nameLen = view.getUint16(p + 28, true);
    const extraLen = view.getUint16(p + 30, true);
    const commentLen = view.getUint16(p + 32, true);
    const offset = view.getUint32(p + 42, true);
    const name = new TextDecoder().decode(new Uint8Array(buf, p + 46, nameLen));
    out.set(name, { method, offset, size });
    p += 46 + nameLen + extraLen + commentLen;
  }
  return out;
}

/** Inflate one entry. The local header repeats the name and extra lengths,
 *  which is what actually locates the compressed bytes. */
async function readEntry(
  buf: ArrayBuffer,
  entry: { method: number; offset: number; size: number },
): Promise<string> {
  const view = dv(buf);
  const p = entry.offset;
  if (view.getUint32(p, true) !== 0x04034b50) throw new Error('Corrupt .xlsx entry.');
  const nameLen = view.getUint16(p + 26, true);
  const extraLen = view.getUint16(p + 28, true);
  const start = p + 30 + nameLen + extraLen;
  const bytes = new Uint8Array(buf, start, entry.size);

  /* 0 = stored, 8 = deflate. Nothing else appears in a spreadsheet written by
     Excel, Sheets or a PSA export. */
  if (entry.method === 0) return new TextDecoder().decode(bytes);
  if (entry.method !== 8) throw new Error(`Unsupported compression in the .xlsx (method ${entry.method}).`);

  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
  return new Response(stream).text();
}

/* -------------------------------------------------------------------------
   Worksheet XML
   ------------------------------------------------------------------------- */

/** "BC12" -> 54. Column letters are base-26 with no zero. */
function colIndex(ref: string): number {
  const letters = /^[A-Z]+/.exec(ref)?.[0] ?? 'A';
  let n = 0;
  for (const ch of letters) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n - 1;
}

const textOf = (el: Element | null): string => (el?.textContent ?? '').trim();

/**
 * Write a numeric cell out in full rather than in scientific notation.
 *
 * Excel stores a large number as `1.2345678901235E+15`, and a ticket number is
 * exactly the kind of long integer that trips it. Passing the raw text through
 * put "1.2345678901235E+15" in the ticket column, which is not a ticket number
 * anybody can look up.
 *
 * Applied ONLY to numeric cells — a text cell keeps its own spelling, so a
 * reference stored as text with leading zeros ("0001234") is untouched, and a
 * genuine decimal stays a decimal.
 */
function expandExponential(raw: string): string {
  if (!/e/i.test(raw)) return raw;
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  /* 'fullwide' never falls back to exponential, and grouping off keeps it a
     number rather than a formatted one. */
  return n.toLocaleString('fullwide', { useGrouping: false, maximumFractionDigits: 20 });
}

function parseSharedStrings(xml: string | null): string[] {
  if (!xml) return [];
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  /* Each <si> is one string, but it can be split across several <t> runs when
     part of it is formatted differently — joining them is the difference
     between "Client Support" and "Client". */
  return [...doc.getElementsByTagName('si')].map((si) =>
    [...si.getElementsByTagName('t')].map((t) => t.textContent ?? '').join(''),
  );
}

function parseSheet(xml: string, shared: string[]): string[][] {
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  const grid: string[][] = [];

  for (const row of [...doc.getElementsByTagName('row')]) {
    const cells: string[] = [];
    for (const c of [...row.getElementsByTagName('c')]) {
      const at = colIndex(c.getAttribute('r') ?? 'A');
      const type = c.getAttribute('t');
      let value: string;
      if (type === 's') {
        /* Shared string: the cell holds an index into the table. */
        value = shared[Number(textOf(c.querySelector('v')))] ?? '';
      } else if (type === 'inlineStr') {
        value = [...c.getElementsByTagName('t')].map((t) => t.textContent ?? '').join('');
      } else {
        value = expandExponential(textOf(c.querySelector('v')));
      }
      /* Sparse: Excel omits empty cells entirely, so pad to the cell's own
         column or every row after a gap would shift left. */
      while (cells.length < at) cells.push('');
      cells[at] = value.trim();
    }
    grid.push(cells);
  }
  return grid;
}

/* -------------------------------------------------------------------------
   CSV
   ------------------------------------------------------------------------- */

/** A quote-aware split. Handles "" escapes and newlines inside quotes, which
 *  a ticket subject line reliably contains. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (quoted) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i += 1;
        } else quoted = false;
      } else cell += ch;
      continue;
    }
    if (ch === '"') quoted = true;
    else if (ch === ',') {
      row.push(cell.trim());
      cell = '';
    } else if (ch === '\n') {
      row.push(cell.trim());
      rows.push(row);
      row = [];
      cell = '';
    } else if (ch !== '\r') cell += ch;
  }
  if (cell || row.length) {
    row.push(cell.trim());
    rows.push(row);
  }
  return rows;
}

/* -------------------------------------------------------------------------
   Entry point
   ------------------------------------------------------------------------- */

/**
 * Turn a picked file into a header row and data rows.
 *
 * The first non-empty row is taken as the header. A ticket export sometimes
 * carries a title line above it, so leading blank rows are skipped rather than
 * being treated as the header and leaving every column unnamed.
 */
export async function readSpreadsheet(file: File): Promise<Sheet> {
  const isCsv = /\.csv$/i.test(file.name);
  let grid: string[][];
  let sheetName: string | null = null;

  if (isCsv) {
    grid = parseCsv(await file.text());
  } else {
    const buf = await file.arrayBuffer();
    const dir = readCentralDirectory(buf);

    /* Resolve the FIRST sheet by name rather than assuming sheet1.xml: an
       export whose first tab was renamed or reordered still has a sheet1.xml,
       and it is not necessarily the one on screen. */
    const wbEntry = dir.get('xl/workbook.xml');
    let target = 'xl/worksheets/sheet1.xml';
    if (wbEntry) {
      const wb = new DOMParser().parseFromString(await readEntry(buf, wbEntry), 'application/xml');
      const first = wb.getElementsByTagName('sheet')[0];
      sheetName = first?.getAttribute('name') ?? null;
      const rid = first?.getAttribute('r:id') ?? first?.getAttribute('id');
      const relsEntry = dir.get('xl/_rels/workbook.xml.rels');
      if (rid && relsEntry) {
        const rels = new DOMParser().parseFromString(
          await readEntry(buf, relsEntry),
          'application/xml',
        );
        const rel = [...rels.getElementsByTagName('Relationship')].find(
          (x) => x.getAttribute('Id') === rid,
        );
        const t = rel?.getAttribute('Target');
        if (t) target = t.startsWith('/') ? t.slice(1) : `xl/${t.replace(/^\.?\//, '')}`;
      }
    }

    const sheetEntry = dir.get(target) ?? dir.get('xl/worksheets/sheet1.xml');
    if (!sheetEntry) throw new Error('That .xlsx has no readable worksheet.');

    const sharedEntry = dir.get('xl/sharedStrings.xml');
    const shared = parseSharedStrings(sharedEntry ? await readEntry(buf, sharedEntry) : null);
    grid = parseSheet(await readEntry(buf, sheetEntry), shared);
  }

  const firstUsed = grid.findIndex((r) => r.some((c) => c !== ''));
  if (firstUsed < 0) return { headers: [], rows: [], sheetName };

  const headers = (grid[firstUsed] ?? []).map((h) => h.trim());
  const rows = grid
    .slice(firstUsed + 1)
    .filter((r) => r.some((c) => c !== ''))
    .map((r) => {
      const out = r.slice(0, headers.length);
      while (out.length < headers.length) out.push('');
      return out;
    });

  return { headers, rows, sheetName };
}
